import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import LoadingScreen from '../../../components/LoadingScreen';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const savedUsername = localStorage.getItem('remembered_username') || '';
  const savedPassword = localStorage.getItem('remembered_password') || '';

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Username or Email or Phone Number is required'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      email: savedUsername,
      password: savedPassword,
    },
  });

  const { setError, handleSubmit, formState: { errors, isSubmitting } } = methods;

  useEffect(() => {
    if (savedUsername && savedPassword) setRememberMe(true);
  }, []); // eslint-disable-line

  const onSubmit = async (data) => {
    try {
      if (rememberMe) {
        localStorage.setItem('remembered_username', data.email);
        localStorage.setItem('remembered_password', data.password);
      } else {
        localStorage.removeItem('remembered_username');
        localStorage.removeItem('remembered_password');
      }
      await login(data.email, data.password);
    } catch (error) {
      if (isMountedRef.current) {
        setError('afterSubmit', error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {isSubmitting && <LoadingScreen />}
      <Stack spacing={2} sx={{ mt: 1.5 }}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.85rem' }}>
            Username, Phone or Email
          </Typography>
          <RHFTextField
            name="email"
            placeholder="Username, Phone or Email"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#D1D5DB' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              },
              '& .MuiInputBase-input': {
                py: 1.2,
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Password</Typography>
            <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}
              sx={{ color: 'primary.main', textDecoration: 'none', fontSize: '0.85rem' }}>
              Forgot Password?
            </Link>
          </Stack>
          <RHFTextField
            name="password"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#D1D5DB' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              },
              '& .MuiInputBase-input': {
                py: 1.2,
                fontSize: '0.875rem',
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify
                      icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                      sx={{ color: 'text.disabled' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              Remember me on this device
            </Typography>
          }
          sx={{ mt: -0.5 }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          mt: 2,
          py: 1.2,
          bgcolor: 'primary.main',
          color: '#fff',
          fontSize: '1rem',
          fontWeight: 700,
          borderRadius: 2,
          '&:hover': { bgcolor: 'primary.dark' },
          boxShadow: 'none',
        }}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
